import * as React from 'react'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import { useSelector } from 'react-redux'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatNormLabel } from '../../utils/normLabels'

/** Colors / spacing from incyia-app-redesign final.html `.rail`. */
const TEXT_DEFAULT = '#AEBAD8'
const TEXT_SELECTED = '#6FE8CC'
const ICON_DEFAULT = '#AEBAD8'
const SELECTED_BG = 'rgba(79, 224, 194, 0.14)'
const SELECTED_BG_HOVER = 'rgba(79, 224, 194, 0.2)'
const HOVER_BG = 'rgba(255, 255, 255, 0.06)'
const ACCENT = '#4FE0C2'

const listItemIconStyles = (_open: boolean) => ({
  minWidth: 27,
  width: 27,
  flex: 'none',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: 'inherit',
  '& svg': { color: 'inherit', width: 16, height: 16, opacity: 0.9 },
  '& svg path[stroke]': { stroke: 'currentColor' },
  '& svg path[fill]:not([fill="none"]):not([fill="white"])': {
    fill: 'currentColor',
  },
})

const listItemButtonStyles = (_open: boolean) => ({
  position: 'relative' as const,
  maxWidth: '100%',
  width: '100%',
  px: '12px',
  py: '10px',
  minHeight: 44,
  borderRadius: '8px',
  my: 0,
  mx: 0,
  gap: '11px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  transition: 'background-color 0.14s ease, color 0.14s ease',
  color: ICON_DEFAULT,
  '&:hover': {
    backgroundColor: HOVER_BG,
    color: '#ffffff',
  },
  '&.Mui-selected': {
    backgroundColor: SELECTED_BG,
    color: TEXT_SELECTED,
    fontWeight: 500,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '-12px',
      top: 8,
      bottom: 8,
      width: 3,
      borderRadius: '0 3px 3px 0',
      backgroundColor: ACCENT,
    },
    '&:hover': {
      backgroundColor: SELECTED_BG_HOVER,
      color: TEXT_SELECTED,
    },
    '& .MuiListItemIcon-root': {
      color: TEXT_SELECTED,
    },
    '& .MuiListItemText-primary': {
      fontWeight: 500,
      color: TEXT_SELECTED,
    },
  },
  '& .MuiListItemText-root': {
    flex: '1 1 auto',
    margin: 0,
    minWidth: 0,
  },
})

const listItemTextPrimarySx = {
  fontFamily: 'Lato, sans-serif',
  fontWeight: 500,
  fontSize: '17px',
  color: TEXT_DEFAULT,
  letterSpacing: '0.01em',
  lineHeight: 1.35,
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const matchesPath = (pathname: string, patterns: RegExp[]) =>
  patterns.some((pattern) => pattern.test(pathname))

const Ic = ({
  children,
}: {
  children: React.ReactNode
}) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden
  >
    {children}
  </svg>
)

const DashboardItem = ({ open }: { open: boolean }) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const dashboardActive = pathname === '/' || pathname === ''

  return (
    <ListItemButton
      component={Link}
      to="/"
      sx={listItemButtonStyles(open)}
      selected={dashboardActive}
      data-tour-id="nav-dashboard"
    >
      <ListItemIcon sx={listItemIconStyles(open)}>
        <Ic>
          <rect x="2" y="2.5" width="5" height="5.5" rx="1.2" />
          <rect x="9" y="2.5" width="5" height="3" rx="1.2" />
          <rect x="9" y="7" width="5" height="6.5" rx="1.2" />
          <rect x="2" y="9.5" width="5" height="4" rx="1.2" />
        </Ic>
      </ListItemIcon>
      {open && (
        <ListItemText
          primary={t('dashboard', { defaultValue: 'Dashboard' })}
          primaryTypographyProps={{ sx: listItemTextPrimarySx }}
        />
      )}
    </ListItemButton>
  )
}

const MainListItems = ({ open }: { open: boolean }) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const pipelinesActive = matchesPath(pathname, [
    /^\/pipelines$/,
    /^\/pipeline\//,
    /^\/mission\/.+\/pipeline\//,
    /^\/addpipeline$/,
  ])
  const projectsActive = matchesPath(pathname, [
    /^\/projets$/,
    /^\/projet\//,
    /^\/mission\/.+\/projet\//,
    /^\/mission\/.+\/addprojet$/,
    /^\/addprojet$/,
  ])
  const tasksActive = matchesPath(pathname, [
    /^\/actions$/,
    /^\/action\//,
    /^\/actiond\//,
    /^\/addaction$/,
    /^\/mission\/.+\/addaction$/,
    /^\/mission\/.+\/projet\/.+\/addaction$/,
  ])
  // Nested /mission/:id/pipeline|projet routes must not also highlight Missions
  const missionsActive =
    matchesPath(pathname, [
      /^\/missions$/,
      /^\/mission-details/,
      /^\/mission-priority/,
      /^\/addmission$/,
      /^\/mission-create$/,
      /^\/mission\/view-analysis\//,
      /^\/mission\/[^/]+$/,
      /^\/mission\/[^/]+\/(addpipeline|addplan|addaudit|addincident)(\/|$)/,
    ]) &&
    !pipelinesActive &&
    !projectsActive &&
    !tasksActive

  return (
    <>
      <ListItemButton
        component={Link}
        to="/missions"
        sx={listItemButtonStyles(open)}
        selected={missionsActive}
        data-tour-id="nav-missions"
      >
        <ListItemIcon sx={listItemIconStyles(open)}>
          <Ic>
            <path d="M9.5 2.5c2.5 0 4 1.5 4 4L9 11l-2.5.5L5 9.5 9.5 5" />
            <path d="M5.5 10.5L3 13" />
            <circle cx="10.5" cy="5.5" r="1" />
          </Ic>
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={t('missions')}
            primaryTypographyProps={{ sx: listItemTextPrimarySx }}
          />
        )}
      </ListItemButton>
      <ListItemButton
        component={Link}
        to="/pipelines"
        sx={listItemButtonStyles(open)}
        selected={pipelinesActive}
        data-tour-id="nav-pipelines"
      >
        <ListItemIcon sx={listItemIconStyles(open)}>
          <Ic>
            <circle cx="4" cy="12" r="2" />
            <circle cx="12" cy="4" r="2" />
            <path d="M4 10V6a2 2 0 012-2h4" />
          </Ic>
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={t('pipelines')}
            primaryTypographyProps={{ sx: listItemTextPrimarySx }}
          />
        )}
      </ListItemButton>
      <ListItemButton
        component={Link}
        to="/projets"
        sx={listItemButtonStyles(open)}
        selected={projectsActive}
        data-tour-id="nav-projects"
      >
        <ListItemIcon sx={listItemIconStyles(open)}>
          <Ic>
            <path d="M2 4.5h4l1.5 2H14V13H2z" />
          </Ic>
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={t('projects')}
            primaryTypographyProps={{ sx: listItemTextPrimarySx }}
          />
        )}
      </ListItemButton>
      <ListItemButton
        component={Link}
        to="/actions"
        sx={listItemButtonStyles(open)}
        selected={tasksActive}
        data-tour-id="nav-actions"
      >
        <ListItemIcon sx={listItemIconStyles(open)}>
          <Ic>
            <path d="M6 4.5h8M6 8h8M6 11.5h5" />
            <path d="M2.5 4.5l1 1 1.5-2M2.5 8l1 1 1.5-2" />
          </Ic>
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={t('tasks')}
            primaryTypographyProps={{ sx: listItemTextPrimarySx }}
          />
        )}
      </ListItemButton>
    </>
  )
}

const MaturityItem = ({ open }: { open: boolean }) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const maturityActive = matchesPath(pathname, [
    /^\/maturite$/,
    /^\/maturity\//,
    /^\/subDomain-Maturityscoring\//,
  ])

  return (
    <ListItemButton
      component={Link}
      to="/maturite"
      sx={listItemButtonStyles(open)}
      selected={maturityActive}
      data-tour-id="nav-maturity"
    >
      <ListItemIcon sx={listItemIconStyles(open)}>
        <Ic>
          <path d="M2 3h12l-4.5 5.5V13L6.5 11.5V8.5z" />
        </Ic>
      </ListItemIcon>
      {open && (
        <ListItemText
          primary={t('maturityManagement')}
          primaryTypographyProps={{ sx: listItemTextPrimarySx }}
        />
      )}
    </ListItemButton>
  )
}

const SecondaryListItems = ({ open }: { open: boolean }) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const auditsActive = matchesPath(pathname, [
    /^\/audits$/,
    /^\/addaudit$/,
    /^\/audit\//,
    /^\/audit-detail\//,
  ])
  const incidentsActive = matchesPath(pathname, [
    /^\/incidents$/,
    /^\/addincident$/,
    /^\/incident\//,
    /^\/incident-detail\//,
  ])
  const risksActive = matchesPath(pathname, [
    /^\/risques$/,
    /^\/addRisque$/,
    /^\/risque\//,
    /^\/risque-detail\//,
  ])
  const plansActive = matchesPath(pathname, [
    /^\/plan$/,
    /^\/addplan$/,
    /^\/plan\//,
    /^\/plan-detail\//,
    /^\/riskTreatmentPlan\//,
  ])

  return (
    <>
      <ListItemButton
        component={Link}
        to="/audits"
        sx={listItemButtonStyles(open)}
        selected={auditsActive}
        data-tour-id="nav-audits"
      >
        <ListItemIcon sx={listItemIconStyles(open)}>
          <Ic>
            <path d="M3 2h6l4 4v8H3z" />
            <path d="M9 2v4h4" />
            <path d="M5.5 9.5h5M5.5 11.5h3" />
          </Ic>
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={t('audits')}
            primaryTypographyProps={{ sx: listItemTextPrimarySx }}
          />
        )}
      </ListItemButton>
      <ListItemButton
        component={Link}
        to="/incidents"
        sx={listItemButtonStyles(open)}
        selected={incidentsActive}
        data-tour-id="nav-incidents"
      >
        <ListItemIcon sx={listItemIconStyles(open)}>
          <Ic>
            <path d="M8 2v5" />
            <path d="M4.5 3.8a5 5 0 107 0" />
          </Ic>
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={t('incidents')}
            primaryTypographyProps={{ sx: listItemTextPrimarySx }}
          />
        )}
      </ListItemButton>
      <ListItemButton
        component={Link}
        to="/risques"
        sx={listItemButtonStyles(open)}
        selected={risksActive}
        data-tour-id="nav-risks"
      >
        <ListItemIcon sx={listItemIconStyles(open)}>
          <Ic>
            <path d="M8 2.2l6 10.6H2z" />
            <path d="M8 6.5v3M8 11.2v.1" />
          </Ic>
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={t('risks')}
            primaryTypographyProps={{ sx: listItemTextPrimarySx }}
          />
        )}
      </ListItemButton>
      <ListItemButton
        component={Link}
        to="/plan"
        sx={listItemButtonStyles(open)}
        selected={plansActive}
        data-tour-id="nav-plans"
      >
        <ListItemIcon sx={listItemIconStyles(open)}>
          <Ic>
            <circle cx="8" cy="8" r="2.2" />
            <path d="M8 1.8v1.6M8 12.6v1.6M1.8 8h1.6M12.6 8h1.6M3.6 3.6l1.1 1.1M11.3 11.3l1.1 1.1M12.4 3.6l-1.1 1.1M4.7 11.3l-1.1 1.1" />
          </Ic>
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={t('treatmentPlan')}
            primaryTypographyProps={{ sx: listItemTextPrimarySx }}
          />
        )}
      </ListItemButton>
    </>
  )
}

const ListItems = ({ open }: { open: boolean }) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const selectedNorm = useSelector(selectSpecifications)[0]?.NormBase || ''
  const normActive =
    selectedNorm.length > 0 && pathname.startsWith(`/security/${selectedNorm}`)
  const dependenciesActive = matchesPath(pathname, [
    /^\/securityFramework$/,
    /^\/security-control-details\//,
    /^\/security-controls\//,
    /^\/security-existing\//,
    /^\/security-subd\//,
    /^\/norms\//,
    /^\/relatedDataMapped\//,
    /^\/securityControlsToMap\//,
    /^\/subdomain\//,
  ])

  return (
    <>
      {selectedNorm && (
        <ListItemButton
          component={Link}
          to={`/security/${selectedNorm}`}
          sx={listItemButtonStyles(open)}
          selected={normActive}
          data-tour-id="nav-norm"
        >
          <ListItemIcon sx={listItemIconStyles(open)}>
            <Ic>
              <path d="M8 1.8l5.2 2v4.3c0 3.1-2.2 5.4-5.2 6.1-3-.7-5.2-3-5.2-6.1V3.8z" />
            </Ic>
          </ListItemIcon>
          {open && (
            <ListItemText
              primary={formatNormLabel(selectedNorm)}
              primaryTypographyProps={{ sx: listItemTextPrimarySx }}
            />
          )}
        </ListItemButton>
      )}

      <ListItemButton
        component={Link}
        to="/securityFramework"
        sx={listItemButtonStyles(open)}
        selected={dependenciesActive}
        data-tour-id="nav-frameworks"
      >
        <ListItemIcon sx={listItemIconStyles(open)}>
          <Ic>
            <path d="M8 1.8l5.2 2v4.3c0 3.1-2.2 5.4-5.2 6.1-3-.7-5.2-3-5.2-6.1V3.8z" />
            <path d="M5.9 7.9l1.5 1.5 2.9-3.1" />
          </Ic>
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={t('sidebar.securityFramework')}
            primaryTypographyProps={{ sx: listItemTextPrimarySx }}
          />
        )}
      </ListItemButton>
    </>
  )
}

export {
  ListItems,
  SecondaryListItems,
  MainListItems,
  MaturityItem,
  DashboardItem,
}
